import { QueryProvider } from "@/app/query";
import { buildRouter } from "@/app/router";
import { SessionProvider, useSession } from "@/app/session";
import { ThemeProvider } from "@/app/theme";
import { Toaster } from "@/components/toaster";
import { CurrentProjectProvider, useCurrentProject } from "@/features/projects/current-project";
import { RouterProvider } from "@tanstack/react-router";

// Build the router exactly once at module level.
//
// TanStack Router v1 keeps singleton reactive stores on the router instance.
// Unmounting <RouterProvider> and re-mounting it (e.g. while session loads) tears
// down those stores, causing:
//   TypeError: Cannot read properties of null (reading 'stores')
//
// Fix: <RouterProvider> is always mounted. Auth/loading/shell layout is handled
// inside the router's root route component (see router.tsx → RootLayout), which
// has full access to SessionProvider and CurrentProjectProvider via React context.
// Live context values (userId etc.) are forwarded via the `context` prop so the
// router never needs to be recreated.
const _router = buildRouter({ userId: "", projectId: null, projectName: null });

function RouterBridge() {
  const { userId, loading } = useSession();
  const { projectId, projectName } = useCurrentProject();

  return (
    <RouterProvider
      router={_router}
      context={{ userId: loading ? "" : (userId ?? ""), projectId, projectName }}
    />
  );
}

export function App() {
  return (
    <ThemeProvider>
      <Toaster />
      <QueryProvider>
        <SessionProvider>
          <CurrentProjectProvider>
            <RouterBridge />
          </CurrentProjectProvider>
        </SessionProvider>
      </QueryProvider>
    </ThemeProvider>
  );
}
